import { AfterViewInit, Component, forwardRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { ApiService }                          from '../../api/api.service';
import { CodeTypeModel }                       from '../../core/models/code-type';
import { EventBusService }                     from '../../core/services/event-bus.service';
import { Table }                               from '../../table/types/table';
import { TableService }                        from '../../table/table.service';
import { TableQueryParameters }                from '../../table/types/table-query-parameters';
import { TransactionCenterFiltersModel }       from '../models/transaction-center.filters';
import { TransactionCenterInspectionModel }    from '../models/transaction-center.inspection';
import { TransactionCenterInspectionsService } from './transaction-center.inspections.service';

@Component({
  selector: 'wor-tc-inspections',
  template: require('./transaction-center.inspections.component.html')
})
export class TransactionCenterInspectionsComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() inspections: Array<TransactionCenterInspectionModel>;
  @Input() statuses: Array<CodeTypeModel>;

  filters: TransactionCenterFiltersModel;
  tableSettings: Table;

  private tableCacheKey: string;
  private subscription: Subscription;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => EventBusService))
    private eventBusService: EventBusService,
    @Inject(forwardRef(() => TransactionCenterInspectionsService)) private inspectionsService: TransactionCenterInspectionsService,
    @Inject(forwardRef(() => TableService)) private tableService: TableService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    this.tableCacheKey = this.uiRouter.stateService.current.data.tableCacheKey;

    this.filters = this.tableService.setFiltersFromCache(this.tableCacheKey, {
      status: 'D',
      value : ''
    });

    this.tableSettings = {
      broadcast: 'filterInspections',
      cache: { enabled: true, key: this.tableCacheKey },
      columns: this.inspectionsService.buildColumns(this.filters),
      empty: { text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INSPECTIONS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
        pageSizes: [10, 25, 50],
        queryCount: () => this.getCount()
      },
      rows: { highlight: { enabled: true } },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, { direction: 'desc', field: 'inspection_date' })
    };

    this.subscription = this.eventBusService.on<string>('searchFilterChanged').subscribe(value => {
      if (!this.uiRouter.stateService.transition) {
        this.filters.value = value;
        this.onFiltersChanged();
      }
    });
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (typeof window['filterInspections'] === 'function') {
        clearInterval(interval);
        this.onFiltersChanged();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onFiltersChanged(): void {
    if (window['filterInspections']) {
      window['filterInspections'](this.filters);
    }
  }

  onStatusChanged(): void {
    this.tableSettings = this.inspectionsService.getUpdatedColumns(this.tableSettings, this.filters);
    this.onFiltersChanged();
  }

  getInspections(params: TableQueryParameters): any {
    return this.inspectionsService.getInspections(this.filters, params)
      .then((inspections: Array<TransactionCenterInspectionModel>) => {
        this.inspections = inspections;
        return inspections;
      });
  }

  getCount(): Promise<number> {
    return this.apiService.get('/transaction_center/inspections_count', [
      this.filters.status || 'D',
      this.filters.value  || '*'
    ]).then((count) => count.inspections_count);
  }

  viewInspection(inspection: TransactionCenterInspectionModel): void {
    this.uiRouter.stateService.go('app.inspection', {
      customer_id: inspection.customer_id,
      vehicle_id : inspection.vehicle_id,
      id         : inspection.id
    });
  }
}
